import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { AxiosError } from 'axios'

import { triggerToast } from '@/components/Toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog'
import { useDeleteCompany } from '@/features/company/hooks'
import { useCompanyStore } from '@/features/company/store'

interface DeleteCompanyDialogProps {
  currentItemCount?: number
  totalItems?: number
}

const DeleteCompanyDialog: React.FC<DeleteCompanyDialogProps> = ({
  currentItemCount = 0,
  totalItems = 0,
}) => {
  const { t } = useTranslation()
  const {
    deletingCompany,
    deleteDialogOpen,
    setDeleteDialogOpen,
    clearDeletingCompany,
    page,
    setPage,
  } = useCompanyStore()

  const deleteMutation = useDeleteCompany()

  const handleOpenChange = (newOpen: boolean) => {
    setDeleteDialogOpen(newOpen)
    if (!newOpen) {
      clearDeletingCompany()
      deleteMutation.reset()
    }
  }

  const handleConfirm = () => {
    if (!deletingCompany) return

    deleteMutation.mutate(deletingCompany.id, {
      onSuccess: () => {
        triggerToast('success', t('company.dialog.messages.deleteSuccess'))
        setDeleteDialogOpen(false)
        clearDeletingCompany()

        // If we're deleting the last item on a page (but not page 1), navigate to previous page
        if (currentItemCount === 1 && page > 1 && totalItems > 1) {
          setPage(page - 1)
        }
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message?: string }>
        const errorMessage =
          axiosError.response?.data?.message ||
          axiosError.message ||
          t('company.dialog.messages.errorGeneric')

        triggerToast('error', errorMessage)
      },
    })
  }

  const isDeleting = useMemo(
    () => deleteMutation.isPending,
    [deleteMutation.isPending]
  )

  const hasProjects = useMemo(
    () => (deletingCompany?.total_projects ?? 0) > 0,
    [deletingCompany?.total_projects]
  )

  return (
    <Dialog open={deleteDialogOpen} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogContent className="sm:max-w-[480px] bg-white shadow-xl rounded-2xl p-6">
          <DialogHeader className="space-y-0.5">
            <DialogTitle className="m-0 font-semibold text-md text-primary-900">
              {t('company.dialog.deleteTitle')}
            </DialogTitle>
            <DialogDescription className="text-sm text-tertiary-600">
              {t('company.dialog.deleteDescription')}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex justify-stretch !space-x-0 gap-lg pt-3">
            <Button
              type="button"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-secondary-700 hover:text-secondary-hover text-sm border-solid border-primary bg-white hover:bg-primary-hover"
              variant="default"
              onClick={() => handleOpenChange(false)}
              disabled={isDeleting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-sm border-solid border-transparent !text-white !bg-error-solid disabled:!bg-disabled disabled:!text-disabled"
              variant="default"
              onClick={handleConfirm}
              disabled={isDeleting || hasProjects}
            >
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export default DeleteCompanyDialog
